import { useEffect, useState } from 'react';
import { Copy, Loader2, Server, ShieldCheck } from 'lucide-react';
import { apiService, PackContainer, PremiumData } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import styles from '@/pages/GradesPage.module.scss';

/** Aperçu d'une texture cosmetic (nametag/ranktag), résolue via le render controller du pack. */
function CosmeticPreview({ containerId, type, name }: { containerId: string; type: 'nametag' | 'ranktag'; name: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!name || !containerId) { setUrl(null); return; }
    let active = true;
    let created: string | null = null;
    setLoading(true);
    apiService.getCosmeticBlobUrl(containerId, type, name).then((r) => {
      if (!active) { if (r) URL.revokeObjectURL(r); return; }
      created = r; setUrl(r); setLoading(false);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [containerId, type, name]);
  return (
    <div className={styles.cosmeticPreview}>
      {loading ? <Loader2 size={16} className={styles.spin} /> : url ? <img src={url} alt={name} /> : <span className={styles.cosmeticMissing}>{name ? 'texture introuvable' : '—'}</span>}
    </div>
  );
}

/** Onglet Apparence d'un grade : nametag (ornement) + ranktag par container, avec aperçu.
 * Enregistrement via la barre du bas (comme blocks/items) ; « Appliquer à tous » reste une action directe. */
export function GradeApparence({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [containers, setContainers] = useState<PackContainer[]>([]);
  const [containerId, setContainerId] = useState('');
  const [data, setData] = useState<PremiumData | null>(null);
  const [loading, setLoading] = useState(false);
  const [nameTag, setNameTag] = useState('');
  const [rankTag, setRankTag] = useState('');
  const [orig, setOrig] = useState({ nameTag: '', rankTag: '' });
  const [applyingAll, setApplyingAll] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setContainers(list);
      setContainerId((prev) => (list.some((c) => c.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId]);

  useEffect(() => {
    if (!containerId) { setData(null); return; }
    let active = true;
    setLoading(true);
    setFeedback(null);
    apiService.getContainerPremium(containerId).then((r) => {
      if (!active) return;
      setData(r.data ?? { found: false, file: null, groups: [], nametags: [], ranktags: [] });
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId]);

  // Charge les valeurs disque + restaure un éventuel brouillon en attente (retour d'onglet/container).
  useEffect(() => {
    const g = data?.groups.find((x) => x.name === grade);
    const diskName = (g?.values?.NameTagCosmetic as string) ?? '';
    const diskRank = (g?.values?.RankTagCosmetic as string) ?? '';
    setOrig({ nameTag: diskName, rankTag: diskRank });
    const pending = getDraftPayload(draftKey('grade-apparence', containerId, grade)) as { nameTag: string; rankTag: string } | undefined;
    setNameTag(pending?.nameTag ?? diskName);
    setRankTag(pending?.rankTag ?? diskRank);
  }, [grade, data, containerId]);

  const container = containers.find((c) => c.id === containerId);
  const dirty = !readOnly && !!data?.found && (nameTag !== orig.nameTag || rankTag !== orig.rankTag);

  const persist = async (ids: string[]) => {
    const values = { NameTagCosmetic: nameTag, RankTagCosmetic: rankTag };
    let ok = 0, fail = 0;
    for (const cid of ids) {
      const res = await apiService.saveContainerPremiumGroup(cid, grade, values);
      if (res.data?.success) ok++; else fail++;
    }
    return { ok, fail };
  };

  const save = async () => {
    const { fail } = await persist([containerId]);
    if (fail === 0) setOrig({ nameTag, rankTag });
    return fail === 0;
  };

  useSnapshotDraft({
    kind: 'grade-apparence',
    groupId: containerId,
    identifier: grade,
    label: `Apparence · ${grade} · ${container?.name ?? containerId}`,
    dirty,
    payload: { nameTag, rankTag },
    save,
  });

  const applyAll = async () => {
    if (!window.confirm(`Appliquer l'apparence du grade « ${grade} » à TOUS les containers (${containers.length}) ?`)) return;
    setApplyingAll(true); setFeedback(null);
    const { ok, fail } = await persist(containers.map((c) => c.id));
    if (fail === 0) setOrig({ nameTag, rankTag });
    setApplyingAll(false);
    setFeedback({ ok: fail === 0, msg: `Appliqué à ${ok} container(s)${fail ? `, ${fail} ignoré(s)` : ''}. Déploie via « Serveurs ».` });
  };

  if (containers.length === 0) return <p className={styles.muted}>Aucun container pour ce projet.</p>;

  return (
    <div className={styles.essBlock}>
      <div className={styles.essBar}>
        <Server size={14} />
        <select className={styles.input} value={containerId} onChange={(e) => setContainerId(e.target.value)}>
          {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !data?.found ? (
        <p className={styles.muted}>Config premium introuvable — synchronise ce container dans « Serveurs ».</p>
      ) : (
        <>
          {readOnly && <div className={styles.protectedNotice}><ShieldCheck size={16} /> Grade protégé : édition réservée aux admins.</div>}
          <div className={styles.essField}>
            <label className={styles.essLabel}>Ornement (NameTag)</label>
            <select className={styles.input} value={nameTag} disabled={readOnly} onChange={(e) => setNameTag(e.target.value)}>
              <option value="">(aucun)</option>
              {data.nametags.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>RankTag</label>
            <select className={styles.input} value={rankTag} disabled={readOnly} onChange={(e) => setRankTag(e.target.value)}>
              <option value="">(aucun)</option>
              {data.ranktags.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className={styles.cosmeticRow}>
            <div className={styles.cosmeticCol}>
              <span className={styles.essLabel}>Aperçu ornement</span>
              <CosmeticPreview containerId={containerId} type="nametag" name={nameTag} />
            </div>
            <div className={styles.cosmeticCol}>
              <span className={styles.essLabel}>Aperçu RankTag</span>
              <CosmeticPreview containerId={containerId} type="ranktag" name={rankTag} />
            </div>
          </div>

          {feedback && <span className={feedback.ok ? styles.okMsg : styles.error}>{feedback.msg}</span>}

          {!readOnly && (
          <div className={styles.essActions}>
            <button className={styles.secondaryBtn} onClick={applyAll} disabled={applyingAll}>
              {applyingAll ? <Loader2 size={14} className={styles.spin} /> : <Copy size={14} />} Appliquer à tous les containers
            </button>
          </div>
          )}
          <p className={styles.muted}>
            {dirty ? 'Modifications en attente — enregistre-les via la barre du bas.' : 'Enregistré.'} Déploie ensuite chaque container via « Serveurs ».
          </p>
        </>
      )}
    </div>
  );
}
